import { supabase } from './supabaseClient';
import { addReorderItem } from './reorder';
import { toHttpUrl } from './url';

// The product catalogue — what's sold, what it costs, and how many are left.
//
// The pure parts (stock state, filtering, totals) take products as arguments
// and touch neither Supabase nor the clock, so they're testable on their own
// (products.test.js). Same split as lib/reorder.js.

// Mirrored from the product spreadsheet, plus the few obvious neighbours a
// skincare list grows into. Not a database check constraint on purpose:
// adding a category here should not require a migration.
export const PRODUCT_CATEGORIES = [
  'Cleanser',
  'Toner',
  'Treatment',
  'Serum',
  'Moisturizer',
  'Masque',
  'Sunscreen',
  'Body',
  'Tools & Accessories',
  'Other',
];

// ---- data ---------------------------------------------------------------

export async function listProducts() {
  const { data, error } = await supabase
    .from('products')
    .select('*, vendor:vendors(id, name)')
    .order('name');
  if (error) throw error;
  return data;
}

export async function addProduct(fields) {
  const { data, error } = await supabase.from('products').insert(fields).select().single();
  if (error) throw error;
  return data;
}

export async function updateProduct(id, updates) {
  const { data, error } = await supabase
    .from('products')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteProduct(id) {
  const { error } = await supabase.from('products').delete().eq('id', id);
  if (error) throw error;
}

// ---- product photos -----------------------------------------------------

const IMAGE_BUCKET = 'product-images';

export function productImageUrl(path) {
  if (!path) return null;
  return supabase.storage.from(IMAGE_BUCKET).getPublicUrl(path).data.publicUrl;
}

// Uploaded under a random UUID rather than the file's own name: two photos
// called "IMG_0042.jpg" must not overwrite each other, and the path is what
// makes a public bucket effectively unguessable.
export async function uploadProductImage(file) {
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const path = `${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from(IMAGE_BUCKET).upload(path, file);
  if (error) throw error;
  return path;
}

// Best-effort: a product row must still save even if the old file can't be
// removed, since an orphaned image is a smaller problem than a failed edit.
export async function removeProductImage(path) {
  if (!path) return;
  await supabase.storage.from(IMAGE_BUCKET).remove([path]);
}

// Stock is written as an absolute count, never as "add 1", because the number
// on screen is what the person at the shelf just counted. `nextStock` clamps
// the arithmetic for the +/- buttons; the write itself is a plain set.
export function nextStock(current, delta) {
  return Math.max(0, Number(current || 0) + delta);
}

export async function setStock(id, quantity) {
  return updateProduct(id, { stock_qty: Math.max(0, Math.round(Number(quantity) || 0)) });
}

// Puts a product on the To Order list. Carries the supplier and the product
// link across so the item arrives with everything a purchase order needs,
// and stamps `product_id` so the catalogue can tell it's already on the list.
export async function addProductToReorder(product, userId) {
  return addReorderItem({
    item_name: product.name,
    product_id: product.id,
    vendor_id: product.vendor_id || null,
    link_url: toHttpUrl(product.product_url),
    created_by: userId,
  });
}

// ---- stock state --------------------------------------------------------

// Three states, and a product is only ever one of them.
//
// `low_stock_at` of null or 0 means "only tell me at zero" rather than being
// a separate untracked state — one fewer thing for the UI to explain, and a
// product you never count still reads as out of stock, which it is.
export const STOCK_STATES = ['out', 'low', 'ok'];

export function stockState(product) {
  const qty = Number(product?.stock_qty ?? 0);
  if (qty <= 0) return 'out';
  return qty <= Number(product?.low_stock_at ?? 0) ? 'low' : 'ok';
}

export const STOCK_TONE = { out: 'danger', low: 'warning', ok: 'success' };

export function stockLabel(product) {
  const state = stockState(product);
  if (state === 'out') return 'Out of stock';
  if (state === 'low') return `Low — ${product.stock_qty} left`;
  return 'In stock';
}

export function needsRestock(product) {
  return stockState(product) !== 'ok';
}

// ---- money --------------------------------------------------------------

function amount(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// What's made on one unit: price − cost, plus that as a share of the price.
//
// Gross margin (over price), not markup (over cost) — the two are routinely
// confused and give very different numbers for the same product, so the UI
// labels it "margin" and this is the definition behind that word.
//
// Null when either side is missing. A product with a price but no recorded
// cost has an *unknown* margin, not a 100% one, and showing 100% because the
// cost field is empty is exactly the kind of number that gets priced on.
export function unitMargin(product) {
  const price = amount(product?.price_jmd);
  const cost = amount(product?.cost_jmd);
  if (price === null || cost === null) return null;
  const profit = price - cost;
  return { profit, percent: price === 0 ? null : (profit / price) * 100 };
}

// ---- derived ------------------------------------------------------------

// What the shelf is worth: on-hand count × retail price, with the same
// multiplication over cost so the page can show what's tied up and what it
// stands to make. Products missing a figure contribute nothing to that figure
// rather than being guessed at, and are counted so the UI can admit the total
// is partial instead of presenting a short number as the whole one.
export function inventoryValue(products) {
  let total = 0;
  let cost = 0;
  let priced = 0;
  let unpriced = 0;
  let costed = 0;

  for (const p of products) {
    const qty = Number(p.stock_qty || 0);
    const price = amount(p.price_jmd);
    const unitCost = amount(p.cost_jmd);

    if (price === null) unpriced += 1;
    else {
      total += price * qty;
      priced += 1;
    }

    if (unitCost !== null) {
      cost += unitCost * qty;
      costed += 1;
    }
  }

  // Only meaningful where both sides are known, so it's reported alongside
  // how many products actually contributed to it.
  const bothKnown = products.filter((p) => unitMargin(p) !== null);
  const potentialProfit = bothKnown.reduce(
    (sum, p) => sum + unitMargin(p).profit * Number(p.stock_qty || 0),
    0
  );

  return {
    total,
    cost,
    priced,
    unpriced,
    costed,
    potentialProfit,
    marginKnownCount: bothKnown.length,
  };
}

export function countByState(products) {
  const counts = { out: 0, low: 0, ok: 0 };
  for (const p of products) counts[stockState(p)] += 1;
  return counts;
}

// Case- and space-insensitive, so "The Ordinary  " and "the ordinary" are the
// same product. Used for the duplicate warning on the add form — a warning,
// not a database constraint: two suppliers can legitimately sell a product of
// the same name, and blocking the save is worse than flagging it.
export function normaliseName(name) {
  return String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function findDuplicate(products, name, exceptId = null) {
  const key = normaliseName(name);
  if (!key) return null;
  return products.find((p) => p.id !== exceptId && normaliseName(p.name) === key) || null;
}

// One filter used by both the table and the export, so what you're looking at
// is exactly what leaves the page.
export function filterProducts(products, { query = '', category = 'All', stock = 'All' } = {}) {
  const q = normaliseName(query);
  return products.filter((p) => {
    if (category !== 'All' && (p.category || 'Other') !== category) return false;
    if (stock === 'Needs restock' && !needsRestock(p)) return false;
    if (stock === 'Out of stock' && stockState(p) !== 'out') return false;
    if (!q) return true;
    // Searched by everything you might remember about it — including the
    // supplier and the blurb, because "the one with licorice root" is a real
    // way to look for a product.
    return normaliseName(
      [p.name, p.category, p.vendor?.name, p.product_info].filter(Boolean).join(' ')
    ).includes(q);
  });
}

export const SORTS = {
  name: (a, b) => a.name.localeCompare(b.name),
  category: (a, b) =>
    (a.category || 'zz').localeCompare(b.category || 'zz') || a.name.localeCompare(b.name),
  supplier: (a, b) =>
    (a.vendor?.name || 'zz').localeCompare(b.vendor?.name || 'zz') || a.name.localeCompare(b.name),
  // Least stock first — the point of sorting by stock is to see what's out.
  stock: (a, b) => Number(a.stock_qty || 0) - Number(b.stock_qty || 0),
  // Most expensive first, and unpriced products sink rather than reading as
  // free.
  price: (a, b) => Number(b.price_jmd || 0) - Number(a.price_jmd || 0),
};

export function sortProducts(products, key = 'name') {
  return [...products].sort(SORTS[key] || SORTS.name);
}

// The catalogue as spreadsheet rows, built from the same filtered list the
// table is showing — so what you export is what you were looking at.
//
// Price and stock export as plain numbers, not "J$2,500": a formatted string
// lands in Sheets as un-summable text, so the currency goes in the header
// instead. Same rule as the Accounting exports.
export function productExportRows(products) {
  return [
    [
      'Category',
      'Product Name',
      'Supplier',
      'Product Information',
      'Cost (JMD)',
      'Price (JMD)',
      'Margin (JMD)',
      'Margin (%)',
      'In Stock',
      'Low Stock At',
      'Stock Status',
      'Product Link',
      'Archived',
    ],
    ...products.map((p) => [
      p.category || '',
      p.name,
      p.vendor?.name || '',
      p.product_info || '',
      p.cost_jmd ?? '',
      p.price_jmd ?? '',
      unitMargin(p)?.profit ?? '',
      // Rounded to one decimal here rather than in the sheet, so the exported
      // column matches what the page showed.
      unitMargin(p)?.percent == null ? '' : Math.round(unitMargin(p).percent * 10) / 10,
      p.stock_qty ?? 0,
      p.low_stock_at ?? '',
      stockLabel(p),
      toHttpUrl(p.product_url) || '',
      p.is_active ? '' : 'Yes',
    ]),
  ];
}
